import { createFileRoute, Link } from "@tanstack/react-router"
import { ArchiveIcon, CookieIcon, InfoIcon, SettingsIcon } from "lucide-react"

import { generateMetaTags } from "@repo/seo/generate-meta-tags"
import { sharedConfig } from "~/lib/config/shared"

export const Route = createFileRoute("/_public-layout/cookie-policy")({
  component: CookiePolicyScreen,
  beforeLoad: async () => {
    return {
      title: "Cookie Policy",
    }
  },
  head: ({ match }) => ({
    meta: generateMetaTags({
      base: {
        title: `${match.context.title} | ${sharedConfig.appName}`,
        description: `Cookie Policy for ${sharedConfig.appName}`,
        url: sharedConfig.baseUrl + match.fullPath,
      },
    }),
  }),
})

function CookiePolicyScreen() {
  return (
    <div className="container mx-auto px-4 py-16 md:px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-10">
          <Link
            to="/"
            className="mb-6 flex items-center gap-2 text-primary hover:underline"
          >
            ← Back to Home
          </Link>
          <h1 className="mb-4 font-bold text-4xl tracking-tight">
            Cookie Policy
          </h1>
          <p className="text-muted-foreground text-xl">
            This policy explains how we use cookies and similar technologies to
            recognize you when you visit our website.
          </p>
        </div>

        <div className="mb-10 rounded-lg bg-muted p-8">
          <div className="mb-4 flex items-center gap-3">
            <CookieIcon className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">What are Cookies?</h2>
          </div>
          <p className="mb-4 text-muted-foreground">
            Cookies are small data files that are placed on your computer or
            mobile device when you visit a website. Cookies are widely used by
            website owners to make their websites work, or to work more
            efficiently, as well as to provide reporting information.
          </p>
          <p className="text-muted-foreground">
            Cookies set by the website owner (in this case,{" "}
            {sharedConfig.appName}) are called "first-party cookies." Cookies
            set by parties other than the website owner are called "third-party
            cookies." Third-party cookies enable third-party features or
            functionality to be provided on or through the website (e.g.,
            advertising, interactive content, and analytics).
          </p>
        </div>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <InfoIcon className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">How We Use Cookies</h2>
          </div>

          <p className="mb-6 text-muted-foreground">
            We use cookies for several reasons. Some cookies are required for
            technical reasons in order for our website to operate, and we refer
            to these as "essential" or "strictly necessary" cookies. Other
            cookies enable us to track and target the interests of our users to
            enhance the experience on our website. Third parties may also serve
            cookies through our website for advertising, analytics, and other
            purposes.
          </p>

          <div className="space-y-6">
            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h3 className="mb-2 font-medium text-lg">Essential Cookies</h3>
              <p className="text-muted-foreground">
                These cookies are strictly necessary to provide you with
                services available through our website and to use some of its
                features, such as access to secure areas. Because these cookies
                are strictly necessary to deliver the website, you cannot refuse
                them without impacting how our website functions.
              </p>
            </div>

            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h3 className="mb-2 font-medium text-lg">
                Performance and Functionality Cookies
              </h3>
              <p className="text-muted-foreground">
                These cookies are used to enhance the performance and
                functionality of our website but are non-essential to their use.
                However, without these cookies, certain functionality may become
                unavailable.
              </p>
            </div>

            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h3 className="mb-2 font-medium text-lg">
                Analytics and Customization Cookies
              </h3>
              <p className="text-muted-foreground">
                These cookies collect information that is used either in
                aggregate form to help us understand how our website is being
                used or how effective our marketing campaigns are, or to help us
                customize our website for you.
              </p>
            </div>

            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h3 className="mb-2 font-medium text-lg">Advertising Cookies</h3>
              <p className="text-muted-foreground">
                These cookies are used to make advertising messages more
                relevant to you. They perform functions like preventing the same
                ad from continuously reappearing, ensuring that ads are properly
                displayed, and in some cases selecting advertisements that are
                based on your interests.
              </p>
            </div>

            <div className="rounded-lg border bg-white p-6 shadow-sm">
              <h3 className="mb-2 font-medium text-lg">Social Media Cookies</h3>
              <p className="text-muted-foreground">
                These cookies are used to enable you to share pages and content
                on our website through third-party social media networks. These
                cookies may also be used for advertising purposes.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <SettingsIcon className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">
              Your Cookie Choices and Control
            </h2>
          </div>

          <p className="mb-4 text-muted-foreground">
            You have the right to decide whether to accept or reject cookies.
            You can exercise your cookie preferences by clicking on the
            appropriate opt-out links provided below.
          </p>

          <div className="space-y-4 text-muted-foreground">
            <p>
              <strong>Browser Controls:</strong> Most web browsers allow you to
              manage your cookie preferences. You can set your browser to refuse
              cookies, or to alert you when cookies are being sent. The Help
              portion of the toolbar on most browsers will tell you how to
              prevent your computer from accepting new cookies, how to have the
              browser notify you when you receive a new cookie, or how to
              disable cookies altogether.
            </p>

            <p>
              <strong>Do Not Track:</strong> Some browsers have a "Do Not Track"
              feature that lets you tell websites that you do not want to have
              your online activities tracked. These features are not yet
              uniform, so we are currently not set up to respond to such
              signals.
            </p>

            <p>
              <strong>Disabling Most Interest Based Advertising:</strong> Most
              advertising networks offer you a way to opt out of Interest Based
              Advertising. You can learn more about it by visiting{" "}
              <a
                href="http://www.aboutads.info/choices/"
                className="text-primary hover:underline"
              >
                http://www.aboutads.info/choices/
              </a>{" "}
              or{" "}
              <a
                href="http://www.youronlinechoices.com/"
                className="text-primary hover:underline"
              >
                http://www.youronlinechoices.com/
              </a>
              .
            </p>
          </div>
        </section>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <ArchiveIcon className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">Cookies We Use</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="mb-6 w-full border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="border p-3 text-left">Cookie Name</th>
                  <th className="border p-3 text-left">Purpose</th>
                  <th className="border p-3 text-left">Duration</th>
                  <th className="border p-3 text-left">Type</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border p-3 text-muted-foreground">_session</td>
                  <td className="border p-3 text-muted-foreground">
                    Used to maintain user session state
                  </td>
                  <td className="border p-3 text-muted-foreground">2 weeks</td>
                  <td className="border p-3 text-muted-foreground">
                    Essential
                  </td>
                </tr>
                <tr>
                  <td className="border p-3 text-muted-foreground">_ga</td>
                  <td className="border p-3 text-muted-foreground">
                    Used to distinguish users for Google Analytics
                  </td>
                  <td className="border p-3 text-muted-foreground">2 years</td>
                  <td className="border p-3 text-muted-foreground">
                    Analytics
                  </td>
                </tr>
                <tr>
                  <td className="border p-3 text-muted-foreground">_gid</td>
                  <td className="border p-3 text-muted-foreground">
                    Used to distinguish users for Google Analytics
                  </td>
                  <td className="border p-3 text-muted-foreground">24 hours</td>
                  <td className="border p-3 text-muted-foreground">
                    Analytics
                  </td>
                </tr>
                <tr>
                  <td className="border p-3 text-muted-foreground">_fbp</td>
                  <td className="border p-3 text-muted-foreground">
                    Used by Facebook to deliver advertisements
                  </td>
                  <td className="border p-3 text-muted-foreground">3 months</td>
                  <td className="border p-3 text-muted-foreground">
                    Advertising
                  </td>
                </tr>
                <tr>
                  <td className="border p-3 text-muted-foreground">
                    cookie_consent
                  </td>
                  <td className="border p-3 text-muted-foreground">
                    Used to remember user's cookie consent choice
                  </td>
                  <td className="border p-3 text-muted-foreground">1 year</td>
                  <td className="border p-3 text-muted-foreground">
                    Essential
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-muted-foreground text-sm">
            This list is not exhaustive and may change from time to time as our
            website or its services evolve.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 font-semibold text-2xl">
            Changes to This Cookie Policy
          </h2>
          <p className="text-muted-foreground">
            We may update our Cookie Policy from time to time. We will notify
            you of any changes by posting the new Cookie Policy on this page and
            updating the "Last Updated" date at the bottom.
          </p>
          <p className="mt-4 text-muted-foreground text-sm">
            Last Updated: June 2023
          </p>
        </section>

        <section className="rounded-lg bg-muted p-8">
          <h2 className="mb-4 font-semibold text-2xl">Contact Us</h2>
          <p className="mb-4 text-muted-foreground">
            If you have any questions about our Cookie Policy, please contact
            us:
          </p>

          <div className="space-y-2 text-muted-foreground">
            <p>Email: privacy@{sharedConfig.appName.toLowerCase()}.com</p>
            <p>
              Address: 123 Innovation Drive, Suite 200, San Francisco, CA 94103
            </p>
          </div>

          <div className="mt-6">
            <div className="flex gap-4">
              <Link
                to="/privacy-policy"
                className="text-primary hover:underline"
                viewTransition
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                className="text-primary hover:underline"
                viewTransition
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
